// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useEffect, useState } from "react";
import { useAuthSession } from "./useAuthSession";
import { getTenantSettings } from "../features/onboarding/api/tenantSettingsApi";
import { isSignedOutError } from "../services/api/demoData";
import { toUserFacingErrorMessage } from "../services/api/userFacingError";
import { getLastKnownTenantId } from "../utils/draftOwner";
import { useRefetchWhenSignedIn } from "../components/common/signedOut/useSignedOutRead";

export interface CurrentTenantState {
  tenantId: string | undefined;
  defaultTimeZone: string | undefined;
  isLoading: boolean;
  error: string | undefined;
  isSignedOut?: boolean;
  retry?: () => void;
}

type TenantLoadState = Omit<CurrentTenantState, "retry">;

export function useCurrentTenant(): CurrentTenantState {
  const { getAccessToken } = useAuthSession();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<TenantLoadState>({
    tenantId: undefined,
    defaultTimeZone: undefined,
    isLoading: true,
    error: undefined,
    isSignedOut: false,
  });

  useEffect(() => {
    let isActive = true;

    async function resolveTenant(): Promise<void> {
      if (attempt > 0) {
        setState((previous) => ({
          ...previous,
          isLoading: true,
          error: undefined,
        }));
      }

      let token: string | undefined;

      try {
        token = await getAccessToken();
      } catch {
        token = undefined;
      }

      try {
        const settings = await getTenantSettings(token ? { token } : {});
        if (isActive) {
          setState({
            tenantId: settings.tenantId,
            defaultTimeZone: settings.settings.workspace?.defaultTimeZone ?? undefined,
            isLoading: false,
            error: undefined,
            isSignedOut: false,
          });
        }
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        if (isSignedOutError(error)) {
          setState((previous) => ({
            tenantId: previous.tenantId ?? getLastKnownTenantId(),
            defaultTimeZone: previous.defaultTimeZone,
            isLoading: false,
            error: undefined,
            isSignedOut: true,
          }));
          return;
        }
        setState({
          tenantId: undefined,
          defaultTimeZone: undefined,
          isLoading: false,
          error: toUserFacingErrorMessage(
            error,
            "Something went wrong loading your workspace. Try again.",
          ),
          isSignedOut: false,
        });
      }
    }

    void resolveTenant();

    return () => {
      isActive = false;
    };
  }, [getAccessToken, attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);
  useRefetchWhenSignedIn(state.isSignedOut === true, retry);

  return { ...state, retry };
}
